import type { ReactNode } from 'react';
import { env } from '../../env';
import { Logo } from '../../components/logo';

/**
 * The frame shared by every signed-out screen: sign in, registration, the password and verification
 * screens, and the payment link page.
 *
 * It follows the marketing site a visitor has just come from: a dark page, the logo with its light
 * wordmark, a large heading ending on a brand-green full stop, and the form in a white card. The
 * form controls stay the dashboard's own light ones, inside the card.
 *
 * The logo sits at the top left, in a header row of its own, and links to the marketing site
 * rather than into the dashboard: a visitor here is signed out, and the site is where they came from.
 */
export function AuthLayout({
  title,
  description,
  children,
  footer,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="flex min-h-dvh flex-col items-center bg-neutral-900 px-4 py-8 sm:px-8 sm:py-10">
      {/* Full width so the logo can sit at the left edge while the form stays centred below. */}
      <header className="flex w-full justify-start">
        <a href={env.PUBLIC_SITE_URL} className="shrink-0">
          <Logo />
        </a>
      </header>

      <main className="my-auto flex w-full max-w-md flex-col gap-8 py-12">
        <div className="text-center">
          <h1 className="text-3xl font-semibold tracking-tight text-balance text-white sm:text-4xl">
            {title}
            {/* Decoration: the site's headings end on a green full stop. Not part of the name. */}
            <span className="text-brand-400" aria-hidden="true">
              .
            </span>
          </h1>
          {description !== undefined && <p className="mt-3 text-neutral-300">{description}</p>}
        </div>

        <div className="rounded-md bg-white p-6 shadow-[0_20px_60px_rgba(0,0,0,0.3)] sm:p-8">
          {children}
        </div>

        {footer !== undefined && (
          // On the dark page the brand-700 link shade is too dark to read, so links here take the
          // bright green instead.
          <div className="text-center text-sm text-neutral-300 [&_.link]:text-brand-400 [&_.link]:decoration-brand-400/40 [&_.link:hover]:text-brand-300 [&_.link:hover]:decoration-brand-300">
            {footer}
          </div>
        )}
      </main>
    </div>
  );
}
